import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useUpdateEffect } from '@react-hookz/web';
import { useConfiguration, useBreakpointDown, Text } from '@travel-frontend/core';
import { useTranslation } from 'next-i18next';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LinkToWidget, PasswordField, PolicyInfo, WidgetLayout } from '../../../components/Authentication/components/index.js';
import { DataOverwriteNotice } from '../../../components/Authentication/components/DataOverwriteNotice.js';
import { ActivateAccountDialog } from '../../../components/Authentication/dialogs/index.js';
import { convertErrorTypeToCamelCase, ERROR_TYPES, validationsConfig } from '../../../components/Authentication/helpers.js';
import { resendVerification, resetErrors as resetApiErrors } from '../../../components/Authentication/redux/actions.js';
import { authState } from '../../../components/Authentication/redux/AuthState.js';
import { useAuthentication, useValidation, useValidationError, useValidationErrorEvaluate } from '../../../hooks/index.js';
import { useTracking } from '../../../hooks/useTracking/useTracking.js';
import { ActionLink, Alert, Button, Stack, TextField } from '../../../primitives/index.js';
import { AUTHENTICATION_QA_ID, qaId } from '../../../utils/qaId.js';
export const SignIn = ({
  onSuccess,
  variant = 'signIn',
  ...restProps
}) => {
  const {
    t
  } = useTranslation('authentication');
  const smDown = useBreakpointDown('sm');
  const dispatch = useDispatch();
  const {
    errorType: error,
    isLoading
  } = useSelector(authState);
  const {
    doLogin,
    toggleWidget,
    isComponentOpen
  } = useAuthentication();
  const [credentials, setCredentials] = useState({
    emailAddress: '',
    password: ''
  });
  const size = smDown ? 'small' : 'medium';
  const {
    validate
  } = useValidation(validationsConfig);
  const {
    errors: validationErrors,
    setFieldErrors,
    resetErrors
  } = useValidationError([]);
  const {
    trackClickEvent
  } = useTracking();
  const {
    isFieldValid
  } = useValidationErrorEvaluate(validationErrors);
  const handleFieldChange = (field, value) => {
    const data = {
      ...credentials,
      [field]: value
    };
    setCredentials(data);
  };
  useUpdateEffect(() => {
    resetErrors();
  }, [isComponentOpen('widget', 'signIn')]);
  const authUrl = useConfiguration('authUrl');
  const handleLogin = useCallback(() => {
    doLogin(credentials, onSuccess, () => toggleWidget('migrateAccount'));
  }, [doLogin, credentials, onSuccess, toggleWidget]);
  const handleSubmit = event => {
    event.preventDefault();
    let isFormValid = true;
    for (const [field, value] of Object.entries(credentials)) {
      const errors = validate([{
        field,
        value
      }]);
      if (errors.length > 0) {
        isFormValid = false;
        setFieldErrors(errors, field);
      }
    }
    if (isFormValid) {
      handleLogin();
    }
  };
  const handleBlur = useCallback(field => () => {
    const data = {
      ...credentials,
      [field]: credentials[field].trim()
    };
    setCredentials(data);
  }, [credentials, setCredentials]);
  const handleFocus = useCallback(field => () => {
    setFieldErrors([], field);
    dispatch(resetApiErrors());
  }, [dispatch, setFieldErrors]);
  const mapErrorToMessage = (field, error) => {
    if (!isFieldValid(field)) {
      return t(`signIn.errors.semantic.${field}`);
    }
    if (error && ERROR_TYPES[field].includes(error)) {
      return t(`signIn.errors.${field}.${convertErrorTypeToCamelCase(error)}`);
    }
    return '';
  };
  const isSubmitDisabled = !credentials.emailAddress || !credentials.password || validationErrors.length > 0 || isLoading || !!error;
  const toggleForgotPassword = () => {
    toggleWidget('forgotPassword');
  };
  return _jsxs(_Fragment, {
    children: [_jsx(WidgetLayout, {
      widget: variant,
      title: t('signIn.ariaTitle'),
      ...restProps,
      children: _jsxs(Stack, {
        spacing: "large",
        alignItems: "center",
        children: [_jsxs(Stack, {
          spacing: "medium",
          alignItems: "center",
          children: [variant === 'sessionExpired' ? _jsxs(_Fragment, {
            children: [_jsx(Text, {
              preset: "headlineLarge",
              children: t('signIn.title_expired')
            }), _jsx(Text, {
              preset: "paragraphMedium",
              color: "contentSecondary",
              children: t('signIn.subtitleExpired')
            })]
          }) : _jsxs(_Fragment, {
            children: [_jsx(Text, {
              preset: "headlineLarge",
              children: t('signIn.title')
            }), _jsx(Text, {
              preset: "paragraphMedium",
              color: "contentSecondary",
              children: t('signIn.subtitle')
            })]
          }), (error === 'invalid-credentials' || error === 'unverified-customer') && _jsx(Alert, {
            variant: "error",
            "data-testid": qaId(AUTHENTICATION_QA_ID, 'signinWidget-unverified-error-alert'),
            children: t(`signIn.errors.hints.${convertErrorTypeToCamelCase(error)}`)
          })]
        }), variant === 'checkoutSignIn' && _jsx(DataOverwriteNotice, {
          variant: "warning",
          withIcon: false,
          children: _jsx(Text, {
            preset: "paragraphSmall",
            children: t('signIn.hint.fillingDataNotice')
          })
        }), _jsx("form", {
          onSubmit: handleSubmit,
          noValidate: true,
          children: _jsxs(Stack, {
            spacing: "medium",
            alignItems: "center",
            children: [_jsx(TextField, {
              name: "email",
              id: "email",
              autoComplete: "username",
              type: "email",
              fullWidth: true,
              onChange: e => handleFieldChange('emailAddress', e.target.value),
              "data-testid": qaId(AUTHENTICATION_QA_ID, 'signinWidget-emailAddress-textfield'),
              label: t('signIn.placeholder.email'),
              size: size,
              invalid: !isFieldValid('emailAddress') || !!error && error !== 'password-attempts-exceeded',
              onFocus: handleFocus('emailAddress'),
              onBlur: handleBlur('emailAddress'),
              message: mapErrorToMessage('emailAddress', error)
            }), _jsx(PasswordField, {
              name: "password",
              id: "password",
              autoComplete: "current-password",
              fullWidth: true,
              size: size,
              onChange: e => handleFieldChange('password', e.target.value),
              label: t('signIn.placeholder.password'),
              onFocus: handleFocus('password'),
              onBlur: handleBlur('password'),
              invalid: !isFieldValid('password') || error === 'invalid-credentials' || error === 'password-attempts-exceeded',
              "data-testid": qaId(AUTHENTICATION_QA_ID, 'signinWidget-currentPassword-textfield'),
              message: mapErrorToMessage('password', error)
            }), _jsx(ActionLink, {
              onClick: toggleForgotPassword,
              "data-testid": qaId(AUTHENTICATION_QA_ID, 'signinWidget-forgotPassword-link'),
              children: t('signIn.forgotPassword')
            }), error === 'unverified-customer' && _jsx(ActionLink, {
              onClick: () => dispatch(
              // @ts-expect-error TS(2345) FIXME
              resendVerification(authUrl, credentials.emailAddress)),
              "data-testid": qaId(AUTHENTICATION_QA_ID, 'signinWidget-activateAccount-link'),
              children: t('signIn.resendVerification')
            }), _jsx(Button, {
              fullWidth: true,
              type: "submit",
              loading: isLoading,
              disabled: isSubmitDisabled,
              "data-testid": qaId(AUTHENTICATION_QA_ID, 'signinWidget-submit-button'),
              children: t('signIn.submit')
            }), _jsx(Alert, {
              caption: t('signIn.hint.caption'),
              children: _jsx(Text, {
                preset: "paragraphMedium",
                children: t('signIn.hint.description')
              })
            }), _jsx(PolicyInfo, {
              onTermsOfUseClick: () => trackClickEvent('terms_of_use'),
              onDataProtectionClick: () => trackClickEvent('data_protection_details')
            }), _jsx(LinkToWidget, {
              onClick: () => trackClickEvent('register_now_2'),
              type: "registration"
            })]
          })
        })]
      })
    }), _jsx(ActivateAccountDialog, {})]
  });
};